
import React, { useMemo, useRef, memo } from 'react'
import { ACHIEVEMENT_REWARDS } from '../../data/rewards'
import { RewardNode } from './RewardNode'
import woodPanel from '../../assets/ui/fantasy/wood_panel.png'

interface AchievementTrackProps {
    currentLevel: number
    famePoints: number
    claimedRewards: number[]
    onClaimReward: (level: number) => void
}

const AchievementTrackComponent: React.FC<AchievementTrackProps> = ({
    currentLevel,
    famePoints,
    claimedRewards,
    onClaimReward
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    // Calculate distinct levels in range [2..11] as per rewards
    const rewards = ACHIEVEMENT_REWARDS

    // Calculate progress percentage for the bar
    // Logic: We have N hops between nodes.
    // Each node is at a certain "step".
    // If currentLevel is between Reward[i] and Reward[i+1], we fill partially.

    // Total steps = number of intervals (rewards.length - 1 + start offset?)
    // Actually, visual line just needs to fill up to the current unlocked node.
    // Let's rely on standard fill:
    // If Lvl 5 is max and we are Lvl 4, we fill past Lvl 4 node towards Lvl 5.

    const totalLevels = 11
    const startLevel = 1

    // A simplistic calculated width for the "blue mercury" line
    // Percentage = (currentLevel - startLevel + partial_progress) / (maxLevel - startLevel)
    // partial_progress = (famePoints % 100) / 100
    // Note: maxLevel in our rewards is 11. 

    const partial = (famePoints >= 1000) ? 1.0 : (famePoints % 100) / 100
    const effectiveLevel = currentLevel + partial

    // Mapping level to horizontal position is tricky without fixed widths.
    // But we use flex box with gaps. Let's approximate purely by status for now, 
    // or use a unified background bar.

    // Better approach for the line: The line is behind the nodes.
    // We can just use a simple CSS progress bar behind the nodes if they are spaced evenly.

    return (
        <div className="w-full relative py-2 select-none">
            {/* Container Frame */}
            <div
                className="relative rounded-lg border-[4px] border-[#3E2723] shadow-inner bg-[#1a0f0a] min-h-[140px] flex items-center"
                style={{ backgroundImage: `url(${woodPanel})`, backgroundSize: 'cover' }}
            >
                <div className="absolute inset-0 bg-black/50 rounded-[4px]"></div>

                <div className="absolute inset-0 bg-black/50"></div>

                {/* Track Container (No Scroll) */}
                <div
                    className="w-full relative px-6 md:px-10 py-6 flex items-center h-full"
                >
                    {/* Background Line */}
                    <div className="absolute left-10 right-10 h-2 bg-[#2b1d0e] border border-[#5D4037] rounded-full top-1/2 transform -translate-y-1/2 z-0"></div>

                    {/* Progress Fill */}
                    {(() => {
                        // Calculate progress based on segments
                        // Segments: 2->3, 3->4, 4->6, 6->8, 8->11
                        const totalSegments = rewards.length - 1
                        let passedSegments = 0
                        let segmentProgress = 0

                        if (currentLevel >= rewards[rewards.length - 1].level) {
                            passedSegments = totalSegments
                        } else if (currentLevel < rewards[0].level) {
                            passedSegments = 0
                        } else {
                            // Find current segment
                            for (let i = 0; i < totalSegments; i++) {
                                const start = rewards[i].level
                                const end = rewards[i + 1].level
                                if (currentLevel >= start && currentLevel < end) {
                                    passedSegments = i

                                    // Calculate partial progress within this segment
                                    // Fame points allow us to be more precise: (level - start) + (fame/100)
                                    // But currentLevel is integer.
                                    // Helper: Effective Level = currentLevel + (fame % 100)/100
                                    const partial = (famePoints >= 1000) ? 1.0 : (famePoints % 100) / 100
                                    const effective = currentLevel + partial

                                    segmentProgress = (effective - start) / (end - start)
                                    break
                                } else if (currentLevel >= end) {
                                    // Fully passed this segment
                                    continue
                                }
                            }
                            // If we are at the exact level of the last node (e.g. 11), loop might not catch it if checking < end
                            // but the first `if` handles >= max level.
                        }

                        const totalProgressPercent = ((passedSegments + segmentProgress) / totalSegments) * 100

                        return (
                            <div
                                className="absolute left-10 h-2 bg-gradient-to-r from-cyan-900 via-cyan-500 to-blue-400 rounded-full top-1/2 transform -translate-y-1/2 z-0 shadow-[0_0_10px_#00FFFF]"
                                style={{
                                    width: `calc(${Math.max(0, Math.min(100, totalProgressPercent))}% - 5rem)`, // -5rem approximates padding offset
                                    transition: 'width 1s ease-out'
                                }}
                            ></div>
                        )
                    })()}

                    {/* Nodes Container - Justify Between */}
                    <div className="w-full flex items-center justify-between z-10">
                        {rewards.map((reward, index) => {
                            const isReached = currentLevel >= reward.level
                            const isClaimed = claimedRewards.includes(reward.level)

                            let status: 'locked' | 'unlocked' | 'claimed' = 'locked'
                            if (isClaimed) status = 'claimed'
                            else if (isReached) status = 'unlocked'

                            return (
                                <RewardNode
                                    key={reward.level}
                                    reward={reward}
                                    status={status}
                                    onClaim={onClaimReward}
                                    isLast={index === rewards.length - 1}
                                />
                            )
                        })}
                    </div>
                </div>

                {/* Title Label (Floating) */}
                <div className="absolute top-2 left-4 px-2 py-0.5 bg-black/60 rounded text-[10px] uppercase tracking-widest text-[#8B4513] font-bold border border-[#8B4513]/30">
                    Path of Legends
                </div>
            </div>
        </div>
    )
}

// Optimization to prevent re-renders on parent updates if props didn't change meaningfully
export const AchievementTrack = memo(AchievementTrackComponent)
